import React, { useState, useEffect } from 'react';
import { Upload, Eye, Edit2, Trash2, Plus, X, FileText } from 'lucide-react';
import apiService from '../config';

const LandAcquisitionTable = () => {
  const [records, setRecords] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [projectId, setProjectId] = useState(null);
  const [proposalId, setProposalId] = useState(null);
  const [docPreviewUrl, setDocPreviewUrl] = useState(null);
  const [isDocModalOpen, setIsDocModalOpen] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [fileError, setFileError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    startDate: '',
    endDate: '',
    file: null,
    projectId: 1429,
    proposalId: 1418
  });
  const openDocumentModal = (url) => {
    if (!url) return;
    setDocPreviewUrl(url);
    setIsDocModalOpen(true);
  };

  const closeDocumentModal = () => {
    setDocPreviewUrl(null);
    setIsDocModalOpen(false);
  };

  useEffect(() => {
    const currentProjectData = localStorage.getItem("currentProjectData");
    const userData = JSON.parse(localStorage.getItem("userData") || "{}");

    const pid =
      currentProjectData
        ? (JSON.parse(currentProjectData).project_id || JSON.parse(currentProjectData).id)
        : (userData?.data?.project_id || userData?.project_id || null);

    const prid =
      userData?.data?.default_proposal ||
      userData?.default_proposal ||
      null;

    setProjectId(pid);
    setProposalId(prid);

    setFormData((prev) => ({
      ...prev,
      projectId: pid,
      proposalId: prid,
    }));
  }, []);


  useEffect(() => {
    if (projectId && proposalId) {
      fetchRecentPublications();
    }
  }, [projectId, proposalId]);

  const formatToDDMMYYYY = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const dd = String(date.getDate()).padStart(2, '0');
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const yyyy = date.getFullYear();
    return `${dd}-${mm}-${yyyy}`;
  };

  const fetchRecentPublications = async () => {
    if (!projectId || !proposalId) return;

    try {
      const res = await apiService.getRecentPublications(projectId, proposalId);

      if (res.status === 200) {
        setRecords(res.data.data || res.data || []);
      }

    } catch (err) {
      console.error('Fetch publication error', err);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // 3. Update the handleFileChange to store the actual File object
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({
        ...prev,
        file
      }));
      setFileError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg('');
    setSuccessMsg('');
    setFileError('');
    if (!formData.file) {
      setFileError("Please upload a document before saving.");
      return;
    } try {
      setIsSubmitting(true);
      const fd = new FormData();
      fd.append('case_no', formData.title);
      fd.append('description', formData.description);
      fd.append('start_date', formData.startDate);
      fd.append('end_date', formData.endDate);
      fd.append('project_id', formData.projectId);
      fd.append('proposal_id', formData.proposalId);
      fd.append('document', formData.file);

      const res = await apiService.postPublication(fd);
      setSuccessMsg(res?.data?.message || 'Record saved successfully');
      setTimeout(() => setSuccessMsg(''), 3000);
      closeModal();
      fetchRecentPublications();
    } catch (err) {
      setErrorMsg(err?.response?.data?.message || 'Save failed. Please contact administrator.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();

    try {
      setIsSubmitting(true);
      const fd = new FormData();
      fd.append('case_no', formData.title);
      fd.append('description', formData.description);
      fd.append('start_date', formData.startDate);
      fd.append('end_date', formData.endDate);
      fd.append('project_id', formData.projectId);
      fd.append('proposal_id', formData.proposalId);

      // only append if user selected a new file
      if (formData.file) {
        fd.append('document', formData.file);
      }

      const res = await apiService.updateRecentPublication(
        editingRecord.publication_id,
        fd
      );

      setSuccessMsg(res?.data?.message || 'Record updated successfully');
      setTimeout(() => setSuccessMsg(''), 3000);
      setErrorMsg('');
      closeModal();
      fetchRecentPublications();
    } catch (err) {
      setErrorMsg(err?.response?.data?.message || 'Update failed. Please contact administrator.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEdit = (record) => {
    setIsDocModalOpen(false);
    setDocPreviewUrl(null);

    setEditingRecord(record);

    setFormData({
      title: record.case_no || "",
      description: record.description || "",
      startDate: record.start_date?.split("T")[0] || "",
      endDate: record.end_date?.split("T")[0] || "",
      file: null,
      projectId: projectId,
      proposalId: proposalId,
    });

    setIsModalOpen(true);
  };

  const handleDelete = async (publicationId) => {
    if (!window.confirm('Are you sure you want to delete this record?')) return;

    try {
      const res = await apiService.deleteRecentPublication(publicationId);

      if (res.status === 200 || res.data?.status === 200) {
        alert(res.data?.message || 'Deleted successfully');
        fetchRecentPublications();
      } else {
        alert('Delete failed');
      }
    } catch (error) {
      console.error('Delete error:', error);
      alert(
        error?.response?.data?.message ||
        'Delete failed. Please contact administrator.'
      );
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingRecord(null);

    setIsDocModalOpen(false);
    setDocPreviewUrl(null);

    setFormData({
      title: "",
      description: "",
      startDate: "",
      endDate: "",
      file: null,
      projectId: projectId,
      proposalId: proposalId,
    });

    setFileError("");
    setErrorMsg("");
  };

  const handleAddNew = () => {
    setIsDocModalOpen(false);
    setDocPreviewUrl(null);

    setEditingRecord(null);

    setFormData({
      title: "",
      description: "",
      startDate: "",
      endDate: "",
      file: null,
      projectId: projectId,
      proposalId: proposalId,
    });

    setIsModalOpen(true);
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="px-6 py-4 rounded-t-lg flex justify-between items-center">
          <h1 className="text-3xl font-bold">Recent Publications</h1>
          <button
            onClick={handleAddNew}
            className="flex text-white items-center gap-2 bg-green-600 hover:bg-green-700 px-4 py-2 rounded-md transition-colors"
          >
            <Plus size={20} />
            Add New Record
          </button>
        </div>
        {successMsg && (
          <div className="mb-4 px-4 py-3 bg-green-100 text-green-800 border border-green-300 rounded-md">
            {successMsg}
          </div>
        )}
        {errorMsg && (
          <div className="mb-4 px-4 py-3 bg-red-100 text-red-800 border border-red-300 rounded-md">
            {errorMsg}
          </div>
        )}
        {/* Table */}
        <div className="bg-white shadow-lg rounded-b-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-blue-500 text-white">
                  <th className="px-6 py-3 text-left text-sm font-semibold">Sr no</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold">LAQ Case No</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold">Description</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold">Start Date</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold">End Date</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold">File</th>
                  <th className="px-6 py-3 text-left text-sm font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {records.map((record, index) => (
                  <tr key={record.publication_id} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                    <td className="px-6 py-4 text-sm text-gray-900">{index + 1}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">{record.case_no}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">{record.description}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">{formatToDDMMYYYY(record.start_date)}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">{formatToDDMMYYYY(record.end_date)}</td>
                    <td className="px-6 py-4">
                      {record.document ? (
                        <button
                          onClick={() => openDocumentModal(record.document)}
                          className="flex items-center gap-2 text-blue-600 hover:text-blue-800"
                        >
                          <Eye size={16} />
                          <span className="text-sm">View</span>
                        </button>
                      ) : (
                        <span className="text-gray-400 text-sm">No File</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleEdit(record)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                          title="Edit"
                        >
                          <Edit2 size={18} />
                        </button>
                        <button
                          onClick={() => handleDelete(record.publication_id)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-md transition-colors"
                          title="Delete"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Modal */}
        {isModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50" onClick={closeModal}>
            {/* <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"> */}
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
              {/* <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"> */}
              {/* Modal Header */}
              <div className="flex justify-between items-center bg-blue-900 text-white px-6 py-4 rounded-t-lg">
                <h2 className="text-xl font-bold">
                  {editingRecord ? 'Edit Record' : 'Add New Record'}
                </h2>
                <button
                  onClick={closeModal}
                  className="hover:bg-blue-800 p-1 rounded transition-colors"
                >
                  <X size={24} />
                </button>
              </div>

              {/* Modal Form */}
              <form
                onSubmit={editingRecord ? handleUpdate : handleSubmit}
                className="p-6 space-y-4"
              >
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    LAQ Case No *
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Description *
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    required
                    rows="4"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>


                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Start Date *
                    </label>
                    <input
                      type="date"
                      name="startDate"
                      value={formData.startDate}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      End Date *
                    </label>
                    <input
                      type="date"
                      name="endDate"
                      value={formData.endDate}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Upload File {editingRecord ? '' : '*'}
                  </label>

                  <div className="flex items-center gap-3">
                    <label className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 cursor-pointer transition-colors">
                      <Upload size={18} />
                      Choose File
                      <input
                        type="file"
                        name="file"
                        onChange={handleFileChange}
                        className="hidden"
                        accept=".pdf,.doc,.docx"
                      />
                    </label>

                    {formData.file && (
                      <span className="text-sm text-gray-600">
                        {formData.file.name}
                      </span>
                    )}
                  </div>

                  {fileError && (
                    <p className="mt-1 text-sm text-red-600">
                      {fileError}
                    </p>
                  )}

                  {editingRecord?.document && (
                    <button
                      type="button"
                      onClick={() => openDocumentModal(editingRecord.document)}
                      className="mt-2 flex items-center gap-2 text-blue-600 hover:text-blue-800 text-sm"
                    >
                      <Eye size={14} />
                      View Existing Document
                    </button>
                  )}
                </div>

                {/* Modal Actions */}
                <div className="flex justify-end gap-3 pt-4 border-t">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Cancel
                  </button>
                  {/* <button
                    type="submit"
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                  >
                    {editingRecord ? 'Update' : 'Save'}
                  </button> */}
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-70 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {isSubmitting ? (
                      <>
                        <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                        </svg>
                        {editingRecord ? 'Updating...' : 'Saving...'}
                      </>
                    ) : (
                      editingRecord ? 'Update' : 'Save'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {isDocModalOpen && (
          <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[60] p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl h-[90vh] flex flex-col">

              {/* Header */}
              <div className="flex justify-between items-center px-4 py-3 bg-blue-900 text-white rounded-t-lg">
                <h3 className="text-lg font-semibold">Document Preview</h3>
                <button
                  onClick={closeDocumentModal}
                  className="hover:bg-blue-800 p-1 rounded"
                >
                  <X size={22} />
                </button>
              </div>

              {/* Body */}
              <div className="flex-1 overflow-hidden">
                <iframe
                  src={docPreviewUrl}
                  title="Document Viewer"
                  className="w-full h-full border-0"
                />
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default LandAcquisitionTable;